import React, { useState, useEffect, useRef } from 'react';

// Search Component
const DashboardSearch = ({
    onSearch,
    status,
    applicationStatuses,
    gender,
    latestCampaignOnly = false,
}) => {
    const autocompleteStatuses = applicationStatuses?.length
        ? applicationStatuses
        : status
            ? [status]
            : [];
    const autocompleteStatusKey = autocompleteStatuses.join(",");
    const [campaignId, setCampaignId] = useState('');
    const [searchFields, setSearchFields] = useState({
        email: '',
        name: '',
        phone_number: '',
        province: '',
    });

    // Autocomplete data
    const [allEmails, setAllEmails] = useState([]);
    const [allNames, setAllNames] = useState([]);
    const [allPhones, setAllPhones] = useState([]);
    const [allCities, setAllCities] = useState([]);

    // Suggestions state for each field
    const [emailSuggestions, setEmailSuggestions] = useState([]);
    const [nameSuggestions, setNameSuggestions] = useState([]);
    const [phoneSuggestions, setPhoneSuggestions] = useState([]);

    // Visibility state for each field's dropdown
    const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
    const [showNameSuggestions, setShowNameSuggestions] = useState(false);
    const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

    // Keyboard navigation index for each field
    const [emailSelectedIndex, setEmailSelectedIndex] = useState(-1);
    const [nameSelectedIndex, setNameSelectedIndex] = useState(-1);
    const [phoneSelectedIndex, setPhoneSelectedIndex] = useState(-1);

    // Refs for click-outside handling
    const emailRef = useRef(null);
    const nameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailSugRef = useRef(null);
    const nameSugRef = useRef(null);
    const phoneSugRef = useRef(null);

    // Fetch all data for autocomplete
    useEffect(() => {
        const fetchAutocompleteData = async () => {
            const token = localStorage.getItem("token");
            const statuses = autocompleteStatusKey ? autocompleteStatusKey.split(",") : [];
            try {
                let latestCampaignId = "";
                if (latestCampaignOnly) {
                    const campaignResponse = await fetch(
                        `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    if (!campaignResponse.ok) {
                        throw new Error(`Failed to fetch latest campaign: ${campaignResponse.status}`);
                    }

                    const campaignData = await campaignResponse.json();
                    const latestCampaign = campaignData.data?.[0];

                    if (!latestCampaign) {
                        throw new Error("No campaign found for search filters");
                    }

                    latestCampaignId = String(latestCampaign.id);
                    setCampaignId(latestCampaignId);
                    localStorage.setItem("campaigin_id", latestCampaignId);
                }
                const query = new URLSearchParams({
                    "pagination[pageSize]": "100",
                    "pagination[page]": "1",
                });
                if (gender) {
                    query.set("filters[gender][$eq]", gender);
                }
                if (statuses.length === 1) {
                    query.set("filters[application_status][$eq]", statuses[0]);
                } else {
                    statuses.forEach((applicationStatus, index) => {
                        query.set(
                            `filters[$or][${index}][application_status][$eq]`,
                            applicationStatus
                        );
                    });
                }
                if (latestCampaignId) {
                    query.set("filters[campaign][id][$eq]", latestCampaignId);
                }
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL_strapi}/applications?${query.toString()}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (response.ok) {
                    const data = await response.json();
                    if (!Array.isArray(data.data) || !Number.isInteger(data.meta?.pagination?.pageCount)) {
                        throw new Error("Unexpected autocomplete applications response");
                    }

                    const additionalPages = await Promise.all(
                        Array.from({ length: data.meta.pagination.pageCount - 1 }, async (_, index) => {
                            const pageQuery = new URLSearchParams(query);
                            pageQuery.set("pagination[page]", String(index + 2));
                            const pageResponse = await fetch(
                                `${process.env.REACT_APP_BASE_URL_strapi}/applications?${pageQuery.toString()}`,
                                {
                                    method: "GET",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${token}`,
                                    },
                                }
                            );

                            if (!pageResponse.ok) {
                                throw new Error(`Failed to fetch autocomplete applications: ${pageResponse.status}`);
                            }

                            const pageData = await pageResponse.json();
                            if (!Array.isArray(pageData.data)) {
                                throw new Error("Unexpected autocomplete applications response");
                            }
                            return pageData.data;
                        })
                    );
                    const applications = data.data.concat(...additionalPages);
                    const emails = [];
                    const names = [];
                    const phones = [];
                    const cities = [];

                    applications.forEach(app => {
                        const attrs = app.attributes || app;
                        if (attrs.email) emails.push(attrs.email);
                        if (attrs.name) names.push(attrs.name);
                        if (attrs.phone_number) phones.push(attrs.phone_number);
                        if (attrs.city) cities.push(attrs.city);
                        if (attrs.temp_city) cities.push(attrs.temp_city);
                    });

                    setAllEmails([...new Set(emails)].sort());
                    setAllNames([...new Set(names)].sort());
                    setAllPhones([...new Set(phones)].sort());
                    setAllCities([...new Set(cities)].sort());
                } else {
                    throw new Error(`Failed to fetch autocomplete applications: ${response.status}`);
                }
            } catch (error) {
                console.error("Error fetching autocomplete data:", error);
            }
        };
        if (autocompleteStatusKey) {
            fetchAutocompleteData();
        }
    }, [autocompleteStatusKey, gender, latestCampaignOnly]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (emailRef.current && !emailRef.current.contains(e.target) &&
                emailSugRef.current && !emailSugRef.current.contains(e.target)) {
                setShowEmailSuggestions(false);
            }
            if (nameRef.current && !nameRef.current.contains(e.target) &&
                nameSugRef.current && !nameSugRef.current.contains(e.target)) {
                setShowNameSuggestions(false);
            }
            if (phoneRef.current && !phoneRef.current.contains(e.target) &&
                phoneSugRef.current && !phoneSugRef.current.contains(e.target)) {
                setShowPhoneSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchFields({ ...searchFields, [name]: value });

        if (name === 'email') {
            if (value.length > 0) {
                const filtered = allEmails.filter(item =>
                    item.toLowerCase().includes(value.toLowerCase())
                );
                setEmailSuggestions(filtered);
                setShowEmailSuggestions(filtered.length > 0);
                setEmailSelectedIndex(-1);
            } else {
                setEmailSuggestions([]);
                setShowEmailSuggestions(false);
            }
        }

        if (name === 'name') {
            if (value.length > 0) {
                const filtered = allNames.filter(item =>
                    item.toLowerCase().includes(value.toLowerCase())
                );
                setNameSuggestions(filtered);
                setShowNameSuggestions(filtered.length > 0);
                setNameSelectedIndex(-1);
            } else {
                setNameSuggestions([]);
                setShowNameSuggestions(false);
            }
        }

        if (name === 'phone_number') {
            if (value.length > 0) {
                const filtered = allPhones.filter(item =>
                    item.includes(value)
                );
                setPhoneSuggestions(filtered);
                setShowPhoneSuggestions(filtered.length > 0);
                setPhoneSelectedIndex(-1);
            } else {
                setPhoneSuggestions([]);
                setShowPhoneSuggestions(false);
            }
        }
    };

    const handleSuggestionClick = (field, value) => {
        setSearchFields({ ...searchFields, [field]: value });
        if (field === 'email') {
            setShowEmailSuggestions(false);
            emailRef.current?.focus();
        } else if (field === 'name') {
            setShowNameSuggestions(false);
            nameRef.current?.focus();
        } else if (field === 'phone_number') {
            setShowPhoneSuggestions(false);
            phoneRef.current?.focus();
        }
    };

    const handleKeyDown = (e, field, suggestions, selectedIndex, setSelectedIndex, setShowSuggestions) => {
        if (!suggestions || suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev =>
                prev < suggestions.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev =>
                prev > 0 ? prev - 1 : suggestions.length - 1
            );
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(field, suggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handleSearch = () => {
        onSearch(searchFields, latestCampaignOnly ? campaignId : undefined);
    };

    const handleClear = () => {
        setSearchFields({
            email: '',
            name: '',
            phone_number: '',
            province: '',
        });
        setEmailSuggestions([]);
        setNameSuggestions([]);
        setPhoneSuggestions([]);
        setShowEmailSuggestions(false);
        setShowNameSuggestions(false);
        setShowPhoneSuggestions(false);
        onSearch(
            { email: '', name: '', phone_number: '', province: '' },
            latestCampaignOnly ? campaignId : undefined
        );
    };

    const highlightMatch = (text, query) => {
        if (!query) return text;
        const index = text.toLowerCase().indexOf(query.toLowerCase());
        if (index === -1) return text;
        return (
            <>
                {text.substring(0, index)}
                <strong>{text.substring(index, index + query.length)}</strong>
                {text.substring(index + query.length)}
            </>
        );
    };

    const renderSuggestions = (suggestions, field, selectedIndex, sugRef, value) => {
        if (suggestions.length === 0) {
            return <li className="ds__suggestions-empty">No matching results</li>;
        }
        return suggestions.map((item, index) => (
            <li
                key={item}
                className={`ds__suggestion-item${index === selectedIndex ? ' active' : ''}`}
                onMouseDown={() => handleSuggestionClick(field, item)}
            >
                {highlightMatch(item, value)}
            </li>
        ));
    };

    return (
        <div className="row">
            {/* Email field with autocomplete */}
            <div className="form-group mb-3 col-sm-12 col-md-6" ref={emailRef}>
                <label htmlFor="email" className="">Email</label>
                <div className="ds__email-wrapper">
                    <input
                        type="email"
                        className="form-control"
                        id="email"
                        placeholder="Start typing to see suggestions..."
                        name="email"
                        value={searchFields.email}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, 'email', emailSuggestions, emailSelectedIndex, setEmailSelectedIndex, setShowEmailSuggestions)}
                        onFocus={() => {
                            if (searchFields.email.length > 0 && emailSuggestions.length > 0) {
                                setShowEmailSuggestions(true);
                            }
                        }}
                        autoComplete="off"
                    />
                    {showEmailSuggestions && (
                        <ul className="ds__suggestions" ref={emailSugRef}>
                            {renderSuggestions(emailSuggestions, 'email', emailSelectedIndex, emailSugRef, searchFields.email)}
                        </ul>
                    )}
                </div>
            </div>

            {/* Name field with autocomplete */}
            <div className="form-group mb-3 col-sm-12 col-md-6" ref={nameRef}>
                <label htmlFor="name" className="">Applicant Name</label>
                <div className="ds__email-wrapper">
                    <input
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder="Start typing to see suggestions..."
                        name="name"
                        value={searchFields.name}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, 'name', nameSuggestions, nameSelectedIndex, setNameSelectedIndex, setShowNameSuggestions)}
                        onFocus={() => {
                            if (searchFields.name.length > 0 && nameSuggestions.length > 0) {
                                setShowNameSuggestions(true);
                            }
                        }}
                        autoComplete="off"
                    />
                    {showNameSuggestions && (
                        <ul className="ds__suggestions" ref={nameSugRef}>
                            {renderSuggestions(nameSuggestions, 'name', nameSelectedIndex, nameSugRef, searchFields.name)}
                        </ul>
                    )}
                </div>
            </div>

            {/* Phone field with autocomplete */}
            <div className="form-group mb-3 col-sm-12 col-md-6" ref={phoneRef}>
                <label htmlFor="phone_number" className="">Mobile Number</label>
                <div className="ds__email-wrapper">
                    <input
                        type="tel"
                        className="form-control"
                        id="phone_number"
                        placeholder="Start typing to see suggestions..."
                        name="phone_number"
                        value={searchFields.phone_number}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, 'phone_number', phoneSuggestions, phoneSelectedIndex, setPhoneSelectedIndex, setShowPhoneSuggestions)}
                        onFocus={() => {
                            if (searchFields.phone_number.length > 0 && phoneSuggestions.length > 0) {
                                setShowPhoneSuggestions(true);
                            }
                        }}
                        autoComplete="off"
                    />
                    {showPhoneSuggestions && (
                        <ul className="ds__suggestions" ref={phoneSugRef}>
                            {renderSuggestions(phoneSuggestions, 'phone_number', phoneSelectedIndex, phoneSugRef, searchFields.phone_number)}
                        </ul>
                    )}
                </div>
            </div>

            <div className="form-group mb-3 col-sm-12 col-md-6">
                <label htmlFor="province" className="">City</label>
                <select
                    className="form-control"
                    id="province"
                    name="province"
                    value={searchFields.province}
                    onChange={handleInputChange}
                >
                    <option value="">Select City</option>
                    {allCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                    ))}
                </select>
            </div>

            <div className="mb-3 col-sm-12 text-right">
                <button onClick={handleSearch} className="btn btn-primary m-1 text-white" disabled={latestCampaignOnly && !campaignId}>Search</button>
                <button onClick={handleClear} className="btn btn-outline-secondary m-1" disabled={latestCampaignOnly && !campaignId}>Clear</button>
            </div>
        </div>
    );
};

export default DashboardSearch;
